import React, { useEffect, useState, useRef } from 'react';
import FilterDropdown from './FilterDropdown';
import ActionButtons from './ActionButtons';
import { useQueryBuilder } from '../context/QueryBuilderContext';
import { FILTER_OPERATORS } from '../../../utils/constants';
import Info from '../../../../Info/Info';
import Paragraph from '../../../../Typography/Paragraph/Paragraph';

interface SpaceFilterDropdownProps {
  linkedSpaces: string[];
  closeDropdown: () => void;
  initalState?: boolean;
  setInitialState: (state: boolean) => void;
  fetchSearchSpaces: (payload: any, headers: any, options?: any) => Promise<any>;
  onApply?: (selectedItems: string[]) => void;
}

const LIMIT = 20;
const DEBOUNCE_DELAY = 300;

const SpaceFilterDropdown: React.FC<SpaceFilterDropdownProps> = ({ linkedSpaces, closeDropdown, initalState, setInitialState, fetchSearchSpaces, onApply }) => {
  const { queryBuilder, setCondition, getConditionValue } = useQueryBuilder();
  const [spaces, setSpaces] = useState<any[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [linkedFetchedSpaces, setLinkedFetchedSpaces] = useState<any[]>([]);

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Debounce logic
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSkip(0);
      setHasMore(true);
      setDebouncedSearchText(searchText);
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timeout);
  }, [searchText]);

  // Reset on debounced search change only
  useEffect(() => {
    setSkip(0);
    setHasMore(true);
  }, [debouncedSearchText]);


  useEffect(() => {
    const fetchSpaces = async () => {
      if (loading || !hasMore) return;

      setLoading(true);

      const query: any = {
        sort: 'title',
        limit: LIMIT,
        skip,
      };

      if (debouncedSearchText) {
        query.search_text = debouncedSearchText;
        query.search_in = 'title';
      }

      try {
        const payload = {
          query: {
            $and: [{
              uid: {
                $in: linkedSpaces,
              },
            }],
          },
          object_type: "space",
          search_text: debouncedSearchText,
          search_field: 'title',
          limit: LIMIT,
          skip,
          fields: [
            'uid',
            'title',
          ]
        }

        const { results: fetchedSpaces = [], count = 0 } = await fetchSearchSpaces(payload, {});
        const matchingLinkedSpaces = fetchedSpaces.filter((space: any) =>
          linkedSpaces?.includes(space.uid)
        );
        setLinkedFetchedSpaces(matchingLinkedSpaces);
        setSpaces((prev) => skip === 0 ? matchingLinkedSpaces : [...prev, ...matchingLinkedSpaces]);
        setHasMore(skip + fetchedSpaces.length < count);
      } catch (err) {
        console.error('Failed to fetch spaces:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSpaces();
  }, [debouncedSearchText, skip]); // Only re-fetch when debounced search or skip changes


  const clearAll = () => {
    setSelectedItems([]);
  };

  const applyFilter = () => {
    if (onApply) {
      // onApply handles both setting space_uid and clearing other filters atomically
      onApply(selectedItems);
    } else {
      if (selectedItems.length > 0) {
        setCondition('space_uid', FILTER_OPERATORS.IN, selectedItems);
        setInitialState(false);
      } else {
        setCondition('space_uid', FILTER_OPERATORS.IN, linkedSpaces);
        setInitialState(true);
      }
    }
    closeDropdown();
  };

  const handleScroll = () => {
    if (!dropdownRef.current || loading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = dropdownRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setSkip((prev) => prev + LIMIT);
    }
  };

  useEffect(() => {
    if (!initalState) {
      const existing = getConditionValue('space_uid');
      if (existing?.[FILTER_OPERATORS.IN]) {
        setSelectedItems(existing[FILTER_OPERATORS.IN]);
      }
    }
  }, [queryBuilder]);

  return (
    <div>
      <input
        type="text"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        placeholder={'Start typing to see results...'}
        style={{ width: '100%', padding: '8px', marginBottom: '8px', boxSizing: 'border-box' }}
      />

      <Info
        testId="cs-entries-recent-searches-info"
        content={
          <div>
            <Paragraph variant="p3" variantStyle="regular" text={'Only spaces linked to this stack are shown here. Modifying space filter will clear rest of the filters.'} />
          </div>
        }
        type="light"
        dismissable={false}
        version="v2"
      />

      <div
        ref={dropdownRef}
        onScroll={handleScroll}
        style={{ maxHeight: '300px', overflowY: 'auto' }}
      >
        <FilterDropdown
          data={spaces.map((space) => ({ name: space.title, id: space.uid }))}
          field="name"
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          loading={loading}
        />
      </div>

      <ActionButtons
        closeDropdown={closeDropdown}
        clearAll={clearAll}
        applyFilter={applyFilter}
      />
    </div>
  );
};

export default SpaceFilterDropdown;
